import { useEffect, useState, useRef, useCallback } from "react";
import { tr } from "@/i18n";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, Plus, Save, CheckCircle, Camera, X, AlertTriangle,
  MapPin, ChevronDown, ChevronRight, Trash2, Clock, Flag,
  FileDown, Mail, BookmarkPlus, WifiOff, CloudUpload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  getInspection, updateInspection, completeInspection, uploadInspectionPhoto,
  createInspectionTemplate, getSites, getTechnicians, getSettings,
} from "@/lib/api";
import { SpeechToTextButton } from "@/components/SpeechToText";
import { downloadPdfUrl } from "@/lib/downloadBlob";
import { compressImage } from "@/lib/imageCompress";
import {
  cachedInspection, draftFor, clearDraft, saveDraft, markCompletedPending,
  storePhotoLocally, localPhotoUrls, isOnline as netOnline, LOCAL_PREFIX,
  initInspectionSync, flush as flushSync, subscribe as subscribeSync,
} from "@/lib/inspectionSync";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ANSWER_OPTIONS = {
  yes_no: [
    { value: "yes", label: "YES", active: "bg-emerald-500 text-white shadow-sm ring-1 ring-emerald-600" },
    { value: "no", label: "NO", active: "bg-red-500 text-white shadow-sm ring-1 ring-red-600" },
  ],
  yes_no_na: [
    { value: "yes", label: "YES", active: "bg-emerald-500 text-white shadow-sm ring-1 ring-emerald-600" },
    { value: "no", label: "NO", active: "bg-red-500 text-white shadow-sm ring-1 ring-red-600" },
    { value: "na", label: "N/A", active: "bg-slate-400 text-white shadow-sm ring-1 ring-slate-500" },
  ],
  pass_fail: [
    { value: "pass", label: "PASS", active: "bg-emerald-500 text-white shadow-sm ring-1 ring-emerald-600" },
    { value: "fail", label: "FAIL", active: "bg-red-500 text-white shadow-sm ring-1 ring-red-600" },
  ],
  pass_fail_na: [
    { value: "pass", label: "PASS", active: "bg-emerald-500 text-white shadow-sm ring-1 ring-emerald-600" },
    { value: "fail", label: "FAIL", active: "bg-red-500 text-white shadow-sm ring-1 ring-red-600" },
    { value: "na", label: "N/A", active: "bg-slate-400 text-white shadow-sm ring-1 ring-slate-500" },
  ],
  na_ok: [
    { value: "ok", label: "OK", active: "bg-emerald-500 text-white shadow-sm ring-1 ring-emerald-600" },
    { value: "na", label: "N/A", active: "bg-slate-400 text-white shadow-sm ring-1 ring-slate-500" },
  ],
};

const FLAG_VALUES = new Set(["no", "fail"]);
const HAS_BUTTONS = new Set(["yes_no", "yes_no_na", "pass_fail", "pass_fail_na", "na_ok"]);

export default function InspectionConduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo;
  const [inspection, setInspection] = useState(null);
  // iter233 — DOT truck/trailer ID lists (Settings → DOT Vehicles).
  const [dotTrucks, setDotTrucks] = useState([]);
  const [dotTrailers, setDotTrailers] = useState([]);
  useEffect(() => {
    getSettings().then(r => {
      setDotTrucks(r.data?.dot_trucks || []);
      setDotTrailers(r.data?.dot_trailers || []);
    }).catch(() => {});
  }, []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeLocIdx, setActiveLocIdx] = useState(0);
  const [showAddLoc, setShowAddLoc] = useState(false);
  const [locForm, setLocForm] = useState({ name: "", identifier: "" });
  const [expandedLocs, setExpandedLocs] = useState({});
  const saveRef = useRef(null);

  // Email / PDF / Save-as-template state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [selectedContactEmail, setSelectedContactEmail] = useState("");
  const [siteContacts, setSiteContacts] = useState([]);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [editingSummary, setEditingSummary] = useState(false);
  const [techList, setTechList] = useState([]);
  const [customSummary, setCustomSummary] = useState("");
  const [customObservations, setCustomObservations] = useState("");
  // ── Offline state ──
  // `online` drives the header chip; `pendingSync` means this inspection
  // is finished on the device and waiting for signal; `localPhotos` maps
  // "local://<id>" refs to object URLs so thumbnails render with no network.
  const [online, setOnline] = useState(netOnline());
  const [pendingSync, setPendingSync] = useState(false);
  const [localPhotos, setLocalPhotos] = useState({});

  useEffect(() => {
    initInspectionSync();
    const up = () => { setOnline(true); flushSync(); };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    // Keep the "awaiting sync" chip honest: it clears the moment the
    // background flush gets an ack and drops the local draft.
    const off = subscribeSync(async () => {
      const d = await draftFor(id);
      setPendingSync(d?.state === "completed_pending");
    });
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
      off();
    };
  }, [id]);

  const refreshLocalPhotos = useCallback(async () => {
    setLocalPhotos(await localPhotoUrls(id));
  }, [id]);

  // Resolve a photo reference: locally-stored blobs render from an
  // object URL, uploaded ones from the backend.
  const photoSrc = useCallback(
    (url) => (String(url).startsWith(LOCAL_PREFIX) ? (localPhotos[url] || "") : `${BACKEND_URL}${url}`),
    [localPhotos]
  );

  useEffect(() => {
    (async () => {
      // Local draft wins: if this device has unsynced work for this
      // inspection, that is the freshest copy by definition.
      const draft = await draftFor(id);
      if (draft?.payload) {
        setInspection(draft.payload);
        setPendingSync(draft.state === "completed_pending");
      }
      try {
        const res = await getInspection(id);
        let insp = res.data;
        // Load site contacts and backfill site_address if missing
        if (insp.client_id) {
          try {
            const sitesRes = await getSites({ client_id: insp.client_id });
            const sites = sitesRes.data || [];
            const matchSite = sites.find(s => s.name === insp.site_name) || sites.find(s => s.is_primary) || sites[0];
            if (matchSite?.contacts) setSiteContacts(matchSite.contacts);
            if (!insp.site_address && matchSite) {
              const addr = [matchSite.address, matchSite.city, matchSite.state, matchSite.zip_code].filter(Boolean).join(", ");
              if (addr) {
                insp = { ...insp, site_address: addr };
                updateInspection(insp.id, { site_address: addr }).catch(() => {});
              }
            }
          } catch {}
        }
        // Don't clobber unsynced local work with the server copy.
        if (!draft?.payload) setInspection(insp);
      } catch {
        // Offline (or the request failed): fall back to the pre-cached
        // copy from the offline bundle.
        if (!draft?.payload) {
          const cached = await cachedInspection(id);
          if (cached) {
            setInspection(cached);
            toast.info("Offline — working from the cached copy");
          } else {
            toast.error("Inspection not available offline");
            navigate(returnTo || "/inspections");
          }
        }
      }
      await refreshLocalPhotos();
      // Load technician list for inspector name dropdown
      try { const techRes = await getTechnicians(); setTechList(techRes.data.filter(t => t.is_active)); } catch {}
      setLoading(false);
    })();
    return () => { if (saveRef.current) clearTimeout(saveRef.current); };
  }, [id, navigate, returnTo, refreshLocalPhotos]);

  const goBack = () => {
    if (returnTo) {
      navigate(returnTo);
    } else if (inspection?.work_order_id && inspection?.client_id) {
      navigate("/clients", { state: { openClientId: inspection.client_id, openTab: "workorders", openWorkOrderId: inspection.work_order_id } });
    } else {
      navigate("/inspections");
    }
  };

  const goBackLabel = returnTo === "/field" ? "Back to Schedule" : inspection?.work_order_id ? "Back to Work Order" : "Back to Inspections";

  const triggerSave = useCallback((data) => {
    if (saveRef.current) clearTimeout(saveRef.current);
    setSaving(true);
    // Write to the device FIRST (synchronously scheduled), so a dead
    // signal or a closed app can never lose an answer.
    saveDraft(data).catch(() => {});
    saveRef.current = setTimeout(async () => {
      try {
        await updateInspection(id, data);
        // Server has it — drop the local draft unless this inspection
        // is queued for sync (completed offline).
        const draft = await draftFor(id);
        if (!draft || draft.state !== "completed_pending") await clearDraft(id);
      } catch { /* stays in the local draft; flushed on reconnect */ }
      setSaving(false);
    }, 1200);
  }, [id]);

  const manualSave = async () => {
    if (saveRef.current) clearTimeout(saveRef.current);
    setSaving(true);
    await saveDraft(inspection);
    try {
      await updateInspection(id, inspection);
      const draft = await draftFor(id);
      if (!draft || draft.state !== "completed_pending") await clearDraft(id);
      toast.success("Saved");
    } catch { toast.success("Saved on this device — will sync when you have signal"); }
    setSaving(false);
  };

  const doComplete = async () => {
    if (saveRef.current) clearTimeout(saveRef.current);
    setSaving(true);
    try {
      await updateInspection(id, inspection);
      const res = await completeInspection(id);
      setInspection(res.data);
      await clearDraft(id);
      setPendingSync(false);
      toast.success("Inspection marked complete");
    } catch {
      // No signal (or the save failed): queue it. Nothing is lost and
      // the tech can close the app.
      await markCompletedPending({ ...inspection, status: "complete" });
      setInspection({ ...inspection, status: "complete" });
      setPendingSync(true);
      toast.success("Completed offline — it will upload automatically when you have signal");
    }
    setSaving(false);
  };

  /* ── Location management ── */
  const addLocation = () => {
    if (!locForm.name.trim()) { toast.error("Name required"); return; }
    const responses = (inspection.template_sections || []).flatMap((s) =>
      (s.checkpoints || []).map((cp) => ({
        section_name: s.name,
        checkpoint_id: cp.id,
        checkpoint_label: cp.label,
        rating_type: cp.rating_type || "yes_no",
        allow_photos: cp.allow_photos !== false,
        allow_notes: cp.allow_notes !== false,
        photo_required_on_fail: cp.photo_required_on_fail || false,
        note_required_on_fail: cp.note_required_on_fail || false,
        value: "",
        flagged: false,
        notes: "",
        photos: [],
      }))
    );
    const loc = {
      id: crypto.randomUUID(),
      name: locForm.name.trim(),
      identifier: locForm.identifier.trim(),
      overall_photos: [],
      overall_notes: "",
      responses,
    };
    const updated = { ...inspection, locations: [...inspection.locations, loc] };
    setInspection(updated);
    setActiveLocIdx(updated.locations.length - 1);
    setShowAddLoc(false);
    setLocForm({ name: "", identifier: "" });
    triggerSave(updated);
  };

  const removeLoc = (idx) => {
    const updated = { ...inspection, locations: inspection.locations.filter((_, i) => i !== idx) };
    setInspection(updated);
    if (activeLocIdx >= updated.locations.length) setActiveLocIdx(Math.max(0, updated.locations.length - 1));
    triggerSave(updated);
  };

  /* ── Response updates ── */
  const updateResp = (ri, field, value) => {
    const updated = { ...inspection };
    const locs = [...updated.locations];
    const loc = { ...locs[activeLocIdx] };
    const resps = [...loc.responses];
    resps[ri] = { ...resps[ri], [field]: value };
    if (field === "value") resps[ri].flagged = FLAG_VALUES.has(value);
    loc.responses = resps;
    locs[activeLocIdx] = loc;
    updated.locations = locs;
    setInspection(updated);
    triggerSave(updated);
  };

  // Photo capture. Compressed on-device first (a 12MP shot is ~4MB and a
  // basement full of them would never sync), then uploaded if there's
  // signal — otherwise stored locally and uploaded on reconnect.
  const captureUrl = async (file) => {
    const compressed = await compressImage(file);
    if (netOnline()) {
      try {
        const res = await uploadInspectionPhoto(compressed);
        return res.data.url;
      } catch { /* fall through to local */ }
    }
    const ref = await storePhotoLocally(id, compressed);
    await refreshLocalPhotos();
    return ref;
  };

  const uploadPhoto = async (ri, file) => {
    try {
      const url = await captureUrl(file);
      const photos = [...(inspection.locations[activeLocIdx].responses[ri].photos || []), url];
      updateResp(ri, "photos", photos);
    } catch { toast.error("Could not save photo"); }
  };

  const removePhoto = (ri, pi) => {
    const photos = inspection.locations[activeLocIdx].responses[ri].photos.filter((_, i) => i !== pi);
    updateResp(ri, "photos", photos);
  };

  const uploadOverallPhoto = async (file) => {
    try {
      const url = await captureUrl(file);
      const updated = { ...inspection };
      const locs = [...updated.locations];
      locs[activeLocIdx] = {
        ...locs[activeLocIdx],
        overall_photos: [...(locs[activeLocIdx].overall_photos || []), url],
      };
      updated.locations = locs;
      setInspection(updated);
      triggerSave(updated);
    } catch { toast.error("Could not save photo"); }
  };

  const removeOverallPhoto = (pi) => {
    const updated = { ...inspection };
    const locs = [...updated.locations];
    locs[activeLocIdx] = {
      ...locs[activeLocIdx],
      overall_photos: locs[activeLocIdx].overall_photos.filter((_, i) => i !== pi),
    };
    updated.locations = locs;
    setInspection(updated);
    triggerSave(updated);
  };

  /* ── Stats ── */
  const getStats = () => {
    let total = 0, flagged = 0, answered = 0;
    (inspection?.locations || []).forEach((l) =>
      (l.responses || []).forEach((r) => { total++; if (r.value) answered++; if (r.flagged) flagged++; })
    );
    return { locations: (inspection?.locations || []).length, total, flagged, answered, passRate: total ? Math.round(((total - flagged) / total) * 100) : 0 };
  };

  const getFlaggedItems = () => {
    const items = [];
    (inspection?.locations || []).forEach((loc) =>
      (loc.responses || []).forEach((r) => {
        if (r.flagged) items.push({ ...r, locName: loc.name, locId: loc.identifier });
      })
    );
    return items;
  };

  /* ── PDF Generation (Server-side) ── */
  const downloadInspectionPDFAction = () => {
    if (!inspection) return;
    downloadPdfUrl(`/inspections/${id}/pdf`);
    toast.success("PDF downloading");
  };

  const handleEmailInspection = () => {
    downloadInspectionPDFAction();
    setTimeout(() => {
      const st = getStats();
      const subject = encodeURIComponent(`Inspection Report: ${inspection.template_name} - ${inspection.conducted_date}`);
      const body = encodeURIComponent(
        `Hi,\n\nPlease find the attached Inspection Report for ${inspection.template_name}.\n\nSite: ${inspection.site_name}\nDate: ${inspection.conducted_date}\nInspector: ${inspection.inspector_name}\nPass Rate: ${st.passRate}%\nFlagged Items: ${st.flagged}\n\nPlease review and let us know if you have any questions.\n\nThank you`
      );
      window.location.href = `mailto:${selectedContactEmail || ""}?subject=${subject}&body=${body}`;
    }, 500);
    setShowEmailDialog(false);
    toast.success("Inspection PDF ready — attach it to the email or send the link when available");
  };

  /* ── Save as Template ── */
  const handleSaveAsTemplate = async () => {
    if (!newTemplateName.trim()) { toast.error("Template name required"); return; }
    try {
      await createInspectionTemplate({
        name: newTemplateName.trim(),
        scope_name: inspection.scope_name || "",
        description: `Created from inspection on ${inspection.conducted_date}`,
        sections: inspection.template_sections || [],
      });
      toast.success("Saved as new template");
      setShowSaveTemplateDialog(false);
      setNewTemplateName("");
    } catch { toast.error("Failed to save template"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400"><Clock className="w-5 h-5 mr-2 animate-spin" />{tr("Loading...")}</div>;
  if (!inspection) return null;

  const isComplete = inspection.status === "complete";
  const stats = getStats();
  const activeLoc = inspection.locations[activeLocIdx];

  /* ═══════════ REPORT VIEW ═══════════ */
  if (isComplete) {
    const flaggedItems = getFlaggedItems();
    const locations = inspection.locations || [];

    // ── Generate Summary Data ──
    // Group flagged items by checkpoint label → locations list
    const criticalByLabel = {};
    flaggedItems.forEach((item) => {
      const key = item.checkpoint_label;
      if (!criticalByLabel[key]) criticalByLabel[key] = [];
      criticalByLabel[key].push({ loc: item.locId || item.locName, notes: item.notes });
    });
    const criticalItems = Object.entries(criticalByLabel).map(([label, entries]) => {
      const locs = entries.map((e) => e.loc).join(", ");
      const notesArr = entries.filter((e) => e.notes?.trim()).map((e) => `${e.loc}: ${e.notes}`);
      return { label, locs, notes: notesArr };
    });

    // Passing locations
    const passingLocs = locations.filter((loc) => (loc.responses || []).every((r) => !r.flagged));
    const observations = [];
    if (passingLocs.length > 0) {
      const names = passingLocs.map((l) => l.identifier || l.name).join(", ");
      observations.push(`${passingLocs.length} of ${locations.length} location${locations.length !== 1 ? "s" : ""} (${names}) passed all inspection items with no issues.`);
    }
    if (locations.length > 0) {
      observations.push(`Overall pass rate of ${stats.passRate}% across ${stats.total} total items checked.`);
    }
    // N/A patterns
    const naByLabel = {};
    locations.forEach((loc) => (loc.responses || []).forEach((r) => {
      if (r.value === "na") { if (!naByLabel[r.checkpoint_label]) naByLabel[r.checkpoint_label] = []; naByLabel[r.checkpoint_label].push(loc.identifier || loc.name); }
    }));
    Object.entries(naByLabel).forEach(([label, locs]) => {
      observations.push(`${label} marked N/A at ${locs.join(", ")}.`);
    });

    // Noteworthy — removed per user request

    // Initialize editable text from auto-generated content
    const autoSummary = `Report overview for ${inspection.template_name} (${inspection.conducted_date}, conducted by ${inspection.inspector_name}): ${stats.locations} location${stats.locations !== 1 ? "s" : ""} inspected with ${stats.total} total items checked. ${stats.flagged > 0 ? `${stats.flagged} item${stats.flagged !== 1 ? "s" : ""} flagged for attention.` : "No items flagged — all checks passed."}`;
    const autoObservations = observations.join("\n");

    const displaySummary = (customSummary || inspection.custom_summary) || autoSummary;
    const displayObservations = (customObservations || inspection.custom_observations) || autoObservations;

    const saveSummaryEdits = async () => {
      try {
        await updateInspection(inspection.id, { custom_summary: customSummary, custom_observations: customObservations });
        setInspection(prev => ({ ...prev, custom_summary: customSummary, custom_observations: customObservations }));
        setEditingSummary(false);
        toast.success("Summary saved");
      } catch { toast.error("Failed to save"); }
    };

    return (
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="inspection-report">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={goBack} className="self-start -ml-1 px-2" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />{goBackLabel}
          </Button>
          {/* Phone: two full-width rows of thumb-sized buttons. Desktop:
              one right-aligned row, unchanged. */}
          <div className="grid grid-cols-2 sm:flex sm:items-center gap-2">
            <Button variant="outline" size="sm" className="h-11 sm:h-8 text-xs sm:text-sm" onClick={() => { setNewTemplateName(inspection.template_name + " (copy)"); setShowSaveTemplateDialog(true); }} data-testid="btn-save-as-template">
              <BookmarkPlus className="w-4 h-4 mr-1" />{tr("Save as Template")}
            </Button>
            <Button variant="outline" size="sm" className="h-11 sm:h-8 text-xs sm:text-sm" onClick={downloadInspectionPDFAction} data-testid="btn-download-inspection-pdf">
              <FileDown className="w-4 h-4 mr-1" />{tr("Export PDF")}
            </Button>
            <Button size="sm" className="h-11 sm:h-8 text-xs sm:text-sm col-span-2 sm:col-auto" onClick={() => {
              const contacts = siteContacts.filter(c => c.email);
              if (contacts.length === 1) setSelectedContactEmail(contacts[0].email);
              else setSelectedContactEmail("");
              setShowEmailDialog(true);
            }} data-testid="btn-email-inspection">
              <Mail className="w-4 h-4 mr-1" />{tr("Email Report")}
            </Button>
          </div>
        </div>

        {/* Header Card */}
        <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6">
          <div className="flex items-start gap-3 sm:gap-5">
            <img src="/abs-logo.png" alt="ABS" className="w-10 h-10 sm:w-14 sm:h-14 object-contain flex-shrink-0 bg-white rounded p-0.5" data-testid="abs-logo" />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-lg sm:text-2xl font-bold text-slate-900 break-words" data-testid="report-title">{inspection.template_name}</h1>
                  <div className="mt-2 space-y-0.5">
                    {inspection.client_name && (
                      <p className="text-sm text-slate-700 font-medium" data-testid="report-client">Customer: {inspection.client_name}</p>
                    )}
                    {inspection.site_name && (
                      <p className="text-sm text-slate-600" data-testid="report-site">Site: {inspection.site_name}</p>
                    )}
                    {inspection.site_address && (
                      <p className="text-sm text-slate-500" data-testid="report-site-address">{inspection.site_address}</p>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    {inspection.conducted_date} &middot; Inspector: {inspection.inspector_name}
                    {inspection.wo_number && <span className="ml-2 text-blue-600 font-medium">WO# {inspection.wo_number}</span>}
                  </p>
                </div>
                {pendingSync ? (
                  <div className="flex flex-col items-end gap-1">
                    <Badge className="bg-amber-100 text-amber-800 border-0 text-xs" data-testid="awaiting-sync-chip">
                      <CloudUpload className="w-3 h-3 mr-1" />{tr("Completed – awaiting sync")}
                    </Badge>
                    {!online && <span className="text-[10px] text-slate-400" data-testid="offline-chip">{tr("Offline — saved on this device")}</span>}
                  </div>
                ) : (
                  <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs" data-testid="synced-chip">{tr("Synced")}</Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ═══ Summary Section ═══ */}
        <div className="bg-white rounded-xl border shadow-sm p-6" data-testid="summary-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-slate-900">{tr("Summary")}</h2>
            {!editingSummary ? (
              <Button variant="ghost" size="sm" className="text-xs text-blue-600" onClick={() => {
                setCustomSummary(displaySummary);
                setCustomObservations(displayObservations);
                setEditingSummary(true);
              }} data-testid="btn-edit-summary">{tr("Edit")}</Button>
            ) : (
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" className="text-xs text-slate-400" onClick={() => setEditingSummary(false)}>{tr("Cancel")}</Button>
                <Button size="sm" className="text-xs h-7" onClick={saveSummaryEdits} data-testid="btn-save-summary">{tr("Save")}</Button>
              </div>
            )}
          </div>

          {editingSummary ? (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-medium text-slate-500">{tr("Summary")}</Label>
                <Textarea value={customSummary} onChange={(e) => setCustomSummary(e.target.value)} rows={4} className="mt-1 text-sm" data-testid="textarea-custom-summary" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-500">{tr("General Observations")}</Label>
                <Textarea value={customObservations} onChange={(e) => setCustomObservations(e.target.value)} rows={4} className="mt-1 text-sm" data-testid="textarea-custom-observations" />
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-500 leading-relaxed whitespace-pre-line">{displaySummary}</p>

              {/* Critical Items */}
              {criticalItems.length > 0 && (
                <div className="mt-5">
                  <h3 className="text-sm font-semibold text-red-600 uppercase tracking-wide" data-testid="critical-items-heading">{tr("Critical Items")}</h3>
                  <ul className="mt-2 space-y-1.5">
                    {criticalItems.map((item) => (
                      <li key={`ci-${item.label}-${item.locs}`} className="flex items-start gap-2 text-sm text-slate-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
                        <div>
                          <span>{item.label} at locations <span className="font-semibold">{item.locs}</span>.</span>
                          {item.notes.length > 0 && (
                            <div className="text-xs text-slate-400 mt-0.5 italic">
                              {item.notes.map((n) => <span key={`cin-${n}`} className="block">{n}</span>)}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* General Observations */}
              <div className="mt-5">
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide" data-testid="observations-heading">{tr("General Observations")}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed whitespace-pre-line">{displayObservations}</p>
              </div>
            </>
          )}
        </div>

        {/* ═══ Flagged Items Count ═══ */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border shadow-sm text-center p-4">
            <div className="text-2xl font-bold">{stats.locations}</div>
            <div className="text-xs text-slate-500 mt-1">{tr("Locations")}</div>
          </div>
          <div className="bg-white rounded-xl border shadow-sm text-center p-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-xs text-slate-500 mt-1">{tr("Items Checked")}</div>
          </div>
          <div className="bg-white rounded-xl border shadow-sm text-center p-4 bg-red-50">
            <div className="text-2xl font-bold text-red-600">{stats.flagged}</div>
            <div className="text-xs text-red-500 mt-1">{tr("Flagged Items")}</div>
          </div>
          <div className="bg-white rounded-xl border shadow-sm text-center p-4 bg-emerald-50">
            <div className="text-2xl font-bold text-emerald-600">{stats.passRate}%</div>
            <div className="text-xs text-emerald-500 mt-1">{tr("Pass Rate")}</div>
          </div>
        </div>

        {/* ═══ Metadata ═══ */}
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-slate-400 text-xs block">{tr("Customer")}</span>
              <p className="font-medium mt-0.5">{inspection.client_name || "\u2014"}</p>
            </div>
            <div>
              <span className="text-slate-400 text-xs block">{tr("Site")}</span>
              <p className="font-medium mt-0.5">{inspection.site_name || "\u2014"}</p>
              {inspection.site_address && <p className="text-xs text-slate-400 mt-0.5">{inspection.site_address}</p>}
            </div>
            <div>
              <span className="text-slate-400 text-xs block">{tr("Conducted on")}</span>
              <p className="font-medium mt-0.5">{inspection.conducted_date || "\u2014"}</p>
            </div>
            <div>
              <span className="text-slate-400 text-xs block">{tr("Inspector")}</span>
              <p className="font-medium mt-0.5">{inspection.inspector_name || "\u2014"}</p>
            </div>
          </div>
          {/* iter233 — DOT audit fields: which truck + trailer was used.
              Options come from Settings → DOT Vehicles. */}
          {inspection.is_dot && (
            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100" data-testid="dot-vehicle-fields">
              <div>
                <Label className="text-xs text-slate-500">{tr("Truck / Van ID")}</Label>
                <Select
                  value={inspection.vehicle_id || ""}
                  onValueChange={(v) => { const updated = { ...inspection, vehicle_id: v }; setInspection(updated); triggerSave(updated); }}
                >
                  <SelectTrigger className="h-9 mt-1" data-testid="dot-truck-select-report"><SelectValue placeholder={tr("Select truck…")} /></SelectTrigger>
                  <SelectContent>
                    {dotTrucks.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">{tr("Trailer ID")}</Label>
                <Select
                  value={inspection.trailer_id || ""}
                  onValueChange={(v) => { const updated = { ...inspection, trailer_id: v }; setInspection(updated); triggerSave(updated); }}
                >
                  <SelectTrigger className="h-9 mt-1" data-testid="dot-trailer-select-report"><SelectValue placeholder={tr("Select trailer…")} /></SelectTrigger>
                  <SelectContent>
                    {dotTrailers.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {/* Flagged Items */}
        {flaggedItems.length > 0 && (
          <div className="bg-white rounded-xl border shadow-sm p-6" data-testid="flagged-section">
            <h2 className="text-lg font-semibold text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />Flagged Items ({flaggedItems.length})
            </h2>
            <div className="space-y-3 mt-4">
              {flaggedItems.map((item) => (
                <div key={`fi-${item.locId || item.locName}-${item.checkpoint_label}`} className="border border-red-100 rounded-lg p-3 bg-red-50/50">
                  <div className="flex items-center gap-1.5 text-xs text-red-400">
                    <MapPin className="w-3 h-3" />{item.locName}{item.locId ? ` (${item.locId})` : ""}
                  </div>
                  <p className="font-medium text-slate-800 mt-1 text-sm">{item.checkpoint_label}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Badge className="bg-red-100 text-red-600 border-0 text-xs">
                      {item.value === "no" ? "No" : item.value === "fail" ? "Fail" : item.value}
                    </Badge>
                    {item.notes && <span className="text-xs text-slate-500 italic">&ldquo;{item.notes}&rdquo;</span>}
                  </div>
                  {item.photos?.length > 0 && (
                    <div className="flex gap-2 mt-2">
                      {item.photos.map((p) => (
                        <img key={`fp-${p}`} src={photoSrc(p)} className="w-16 h-16 object-cover rounded border" alt="" />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Full Detail */}
        <div className="bg-white rounded-xl border shadow-sm p-6" data-testid="detail-section">
          <h2 className="text-lg font-semibold text-slate-900">{tr("Inspection Detail")}</h2>
          <div className="space-y-2 mt-4">
            {inspection.locations.map((loc) => {
              const locFlagged = (loc.responses || []).filter((r) => r.flagged).length;
              const expanded = expandedLocs[loc.id];
              return (
                <div key={loc.id} className="border rounded-lg overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-3 hover:bg-slate-50 text-left"
                    onClick={() => setExpandedLocs((p) => ({ ...p, [loc.id]: !p[loc.id] }))}
                  >
                    <div className="flex items-center gap-2">
                      {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      <span className="font-medium text-sm">{loc.name}</span>
                      {loc.identifier && <span className="text-xs text-slate-400">({loc.identifier})</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {locFlagged > 0 && <Badge className="bg-red-100 text-red-600 border-0 text-xs">{locFlagged} flagged</Badge>}
                      <span className="text-xs text-slate-400">{loc.responses?.length || 0} items</span>
                    </div>
                  </button>
                  {expanded && (
                    <div className="border-t px-4 pb-3 space-y-1.5 pt-2">
                      {loc.overall_photos?.length > 0 && (
                        <div className="flex gap-2 mb-3">
                          {loc.overall_photos.map((p) => (
                            <img key={`lop-${p}`} src={photoSrc(p)} className="w-20 h-20 object-cover rounded border" alt="" />
                          ))}
                        </div>
                      )}
                      {(loc.responses || []).map((r, ri) => (
                        // eslint-disable-next-line react/no-array-index-key -- responses keep stable order in the source location.responses array
                        <div key={`resp-${ri}`} className={`flex items-start gap-3 p-2 rounded text-sm ${r.flagged ? "bg-red-50 border border-red-100" : ""}`}>
                          <span className="text-xs text-slate-400 font-mono w-5 mt-0.5">{ri + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-slate-700">{r.checkpoint_label}</p>
                            {r.rating_type === "long_text" && r.value && <p className="text-xs text-slate-500 mt-0.5">{r.value}</p>}
                            {r.rating_type === "text" && r.value && <p className="text-xs text-slate-500 mt-0.5">{r.value}</p>}
                            {r.notes && <p className="text-xs text-slate-400 mt-0.5 italic">&ldquo;{r.notes}&rdquo;</p>}
                            {r.photos?.length > 0 && (
                              <div className="flex gap-1.5 mt-1.5">
                                {r.photos.map((p) => (
                                  <img key={`rp-${p}`} src={photoSrc(p)} className="w-12 h-12 object-cover rounded border" alt="" />
                                ))}
                              </div>
                            )}
                          </div>
                          {HAS_BUTTONS.has(r.rating_type) && r.value ? (
                            <Badge className={`border-0 text-xs flex-shrink-0 ${FLAG_VALUES.has(r.value) ? "bg-red-100 text-red-600" : r.value === "na" ? "bg-slate-100 text-slate-500" : "bg-emerald-100 text-emerald-600"}`}>
                              {r.value.charAt(0).toUpperCase() + r.value.slice(1)}
                            </Badge>
                          ) : !HAS_BUTTONS.has(r.rating_type) ? null : (
                            <Badge className="bg-slate-100 text-slate-400 border-0 text-xs">&mdash;</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Email Dialog */}
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{tr("Email Inspection Report")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">The inspection PDF will be downloaded and your email client will open. Attach the PDF to complete.</p>
              <div>
                <Label className="text-xs font-medium">{tr("Send To")}</Label>
                {siteContacts.filter(c => c.email).length > 0 ? (
                  <div className="space-y-2 mt-2">
                    {siteContacts.filter(c => c.email).map(contact => (
                      <label key={contact.id || contact.email} className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${selectedContactEmail === contact.email ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                        <input type="radio" name="insp-contact-email" value={contact.email} checked={selectedContactEmail === contact.email} onChange={(e) => setSelectedContactEmail(e.target.value)} className="accent-blue-600" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{contact.name} {contact.is_primary && <span className="text-xs text-blue-600">(Primary)</span>}</div>
                          <div className="text-xs text-slate-500">{contact.email}</div>
                        </div>
                      </label>
                    ))}
                    <div className="pt-2">
                      <Label className="text-xs">{tr("Or Enter Email Manually")}</Label>
                      <Input type="email" value={siteContacts.some(c => c.email === selectedContactEmail) ? "" : selectedContactEmail} onChange={(e) => setSelectedContactEmail(e.target.value)} placeholder="email@example.com" className="mt-1" data-testid="input-insp-manual-email" />
                    </div>
                  </div>
                ) : (
                  <div className="mt-2">
                    <Input type="email" value={selectedContactEmail} onChange={(e) => setSelectedContactEmail(e.target.value)} placeholder="email@example.com" data-testid="input-insp-manual-email" />
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowEmailDialog(false)}>{tr("Cancel")}</Button>
                <Button onClick={handleEmailInspection} data-testid="btn-send-inspection-email"><Mail className="w-4 h-4 mr-1" />{tr("Download PDF & Open Email")}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Save as Template Dialog */}
        <Dialog open={showSaveTemplateDialog} onOpenChange={setShowSaveTemplateDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>{tr("Save as New Template")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Template Name *</Label>
                <Input value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)} placeholder="e.g., DIA Trash Chute v2" data-testid="input-new-template-name" />
              </div>
              <p className="text-xs text-slate-400">This will create a new template with the same sections and checkpoints as this inspection.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveTemplateDialog(false)}>{tr("Cancel")}</Button>
              <Button onClick={handleSaveAsTemplate} data-testid="btn-confirm-save-template">{tr("Save Template")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  /* ═══════════ CONDUCT VIEW ═══════════ */
  return (
    <div className="space-y-4 max-w-5xl mx-auto px-1 sm:px-0" data-testid="inspection-conduct">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={goBack} className="self-start -ml-1 px-2" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />{goBackLabel}
          </Button>
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-slate-900">{inspection.template_name}</h1>
            <p className="text-xs text-slate-500">
              {[inspection.client_name, inspection.site_name, inspection.site_address]
                .filter(Boolean).join(" · ")}
            </p>
            <p className="text-xs text-slate-400">
              {inspection.conducted_date ? <>{inspection.conducted_date} &middot; </> : null}
              Inspector: {inspection.inspector_name || (
                <Select value="__pick__" onValueChange={(v) => {
                  if (v === "__pick__") return;
                  const updated = { ...inspection, inspector_name: v };
                  setInspection(updated);
                  updateInspection(inspection.id, { inspector_name: v }).catch(() => {});
                }}>
                  <SelectTrigger className="h-5 w-auto min-w-[100px] text-[11px] border-dashed inline-flex px-1.5 py-0" data-testid="select-inspector">
                    <SelectValue placeholder={tr("Select inspector")} />
                  </SelectTrigger>
                  <SelectContent>
                    {techList.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto sm:ml-0">
          {saving && <span className="text-xs text-amber-500 animate-pulse">{tr("Saving...")}</span>}
          {!online && (
            <Badge className="bg-slate-800 text-slate-100 border-0 text-xs" data-testid="offline-chip">
              <WifiOff className="w-3 h-3 mr-1" />{tr("Offline")}
            </Badge>
          )}
          {pendingSync ? (
            <Badge className="bg-amber-100 text-amber-800 border-0 text-xs" data-testid="awaiting-sync-chip">
              <CloudUpload className="w-3 h-3 mr-1" />{tr("Awaiting sync")}
            </Badge>
          ) : inspection.status === "complete" ? (
            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs" data-testid="synced-chip">{tr("Synced")}</Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">{tr("In Progress")}</Badge>
          )}
          <Button variant="outline" size="sm" onClick={manualSave} data-testid="btn-save">
            <Save className="w-4 h-4 mr-1" />{tr("Save")}
          </Button>
          <Button size="sm" onClick={doComplete} data-testid="btn-complete">
            <CheckCircle className="w-4 h-4 mr-1" />{tr("Complete")}
          </Button>
        </div>
      </div>

      {/* iter233 — DOT audit fields (conducting view): which truck +
          trailer is being used. Options from Settings → DOT Vehicles. */}
      {inspection.is_dot && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 grid grid-cols-2 gap-3" data-testid="dot-vehicle-fields-conduct">
          <div>
            <Label className="text-xs text-amber-800 font-semibold">{tr("Truck / Van ID")}</Label>
            <Select
              value={inspection.vehicle_id || ""}
              onValueChange={(v) => { const updated = { ...inspection, vehicle_id: v }; setInspection(updated); triggerSave(updated); }}
            >
              <SelectTrigger className="h-9 mt-1 bg-white" data-testid="dot-truck-select"><SelectValue placeholder={tr("Select truck…")} /></SelectTrigger>
              <SelectContent>
                {dotTrucks.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-amber-800 font-semibold">{tr("Trailer ID")}</Label>
            <Select
              value={inspection.trailer_id || ""}
              onValueChange={(v) => { const updated = { ...inspection, trailer_id: v }; setInspection(updated); triggerSave(updated); }}
            >
              <SelectTrigger className="h-9 mt-1 bg-white" data-testid="dot-trailer-select"><SelectValue placeholder={tr("Select trailer…")} /></SelectTrigger>
              <SelectContent>
                {dotTrailers.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Location Bar */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="location-bar">
        <Button variant="outline" size="sm" onClick={() => setShowAddLoc(true)} className="flex-shrink-0" data-testid="btn-add-location">
          <Plus className="w-4 h-4 mr-1" />{tr("Location")}
        </Button>
        {inspection.locations.map((loc, i) => {
          const ans = loc.responses.filter((r) => r.value).length;
          const tot = loc.responses.length;
          const fl = loc.responses.filter((r) => r.flagged).length;
          return (
            <button
              key={loc.id}
              onClick={() => setActiveLocIdx(i)}
              data-testid={`loc-tab-${i}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border whitespace-nowrap transition-all flex-shrink-0 ${
                i === activeLocIdx
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}
            >
              <span className="font-medium">{loc.name}</span>
              {loc.identifier && (
                <span className={i === activeLocIdx ? "text-slate-400" : "text-slate-400"}>{loc.identifier}</span>
              )}
              <span className={`text-xs ${i === activeLocIdx ? "text-slate-400" : "text-slate-400"}`}>
                {ans}/{tot}
              </span>
              {fl > 0 && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
            </button>
          );
        })}
      </div>

      {/* Active Location */}
      {activeLoc ? (
        <div className="space-y-3">
          {/* Location header */}
          <div className="flex items-center justify-between bg-white rounded-lg border p-3">
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">{activeLoc.name}</span>
                {activeLoc.identifier && <span className="text-xs text-slate-400">({activeLoc.identifier})</span>}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => removeLoc(activeLocIdx)} className="text-red-400 hover:text-red-600" data-testid="btn-remove-location">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          {/* Overall Photos */}
          <div className="bg-white rounded-lg border p-3">
            <Label className="text-xs text-slate-500 font-semibold uppercase tracking-wide">{tr("Overall Photos")}</Label>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {(activeLoc.overall_photos || []).map((url, pi) => (
                // eslint-disable-next-line react/no-array-index-key -- photo URLs can repeat (same image uploaded twice); removeOverallPhoto uses index
                <div key={`olp-${pi}`} className="relative w-20 h-20 group">
                  <img src={photoSrc(url)} className="w-full h-full object-cover rounded-md border" alt="" />
                  <button
                    onClick={() => removeOverallPhoto(pi)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <label
                className="w-20 h-20 border-2 border-dashed border-slate-200 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                data-testid="upload-overall-photo"
              >
                <Camera className="w-5 h-5 text-slate-400" />
                <span className="text-[10px] text-slate-400 mt-0.5">{tr("Add Photo")}</span>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files?.[0] && uploadOverallPhoto(e.target.files[0])} />
              </label>
            </div>
          </div>

          {/* Checkpoints */}
          <div className="space-y-2">
            {activeLoc.responses.map((resp, ri) => {
              const opts = ANSWER_OPTIONS[resp.rating_type] || ANSWER_OPTIONS.yes_no;
              const showSectionHeader = ri === 0 || resp.section_name !== activeLoc.responses[ri - 1]?.section_name;
              const hasButtons = HAS_BUTTONS.has(resp.rating_type);
              const isPhotoField = resp.rating_type === "photo";
              const isTextField = resp.rating_type === "text" || resp.rating_type === "long_text";
              const isFailed = FLAG_VALUES.has(resp.value);
              const needsPhoto = isFailed && resp.photo_required_on_fail && (!resp.photos || resp.photos.length === 0);
              const needsNote = isFailed && resp.note_required_on_fail && !resp.notes?.trim();
              return (
                // eslint-disable-next-line react/no-array-index-key -- checkpoint responses are stable positions in the inspection template; updates use ri
                <div key={`cp-${ri}`}>
                  {showSectionHeader && resp.section_name && (
                    <div className="mt-4 mb-2">
                      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{resp.section_name}</div>
                    </div>
                  )}
                  <div
                    className={`bg-white rounded-lg border p-4 transition-colors ${resp.flagged ? "border-red-200 bg-red-50/30" : ""}`}
                    data-testid={`checkpoint-card-${ri}`}
                  >
                    {/* Question */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2 flex-1">
                        <span className="text-xs text-slate-400 font-mono w-6 mt-0.5 flex-shrink-0">{ri + 1}.</span>
                        <p className="text-sm font-medium text-slate-800">{resp.checkpoint_label}</p>
                      </div>
                      {resp.flagged && <Flag className="w-4 h-4 text-red-500 flex-shrink-0" />}
                    </div>

                    {/* Answer Buttons — only for rating types */}
                    {hasButtons && (
                      <div className="flex items-center gap-2 mt-3 ml-6 sm:ml-8">
                        {opts.map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => updateResp(ri, "value", resp.value === opt.value ? "" : opt.value)}
                            data-testid={`answer-${ri}-${opt.value}`}
                            className={`px-5 py-3 sm:py-2.5 rounded-lg text-sm font-bold transition-all touch-manipulation ${
                              resp.value === opt.value
                                ? opt.active
                                : "bg-white border-2 border-slate-200 text-slate-400 hover:border-slate-300"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Photo-only field */}
                    {isPhotoField && (
                      <div className="mt-3 ml-6 sm:ml-8">
                        <div className="flex items-center gap-2 flex-wrap">
                          {(resp.photos || []).map((url, pi) => (
                            // eslint-disable-next-line react/no-array-index-key -- photo URLs can repeat; removePhoto uses (ri, pi) tuple
                            <div key={`pf-${pi}`} className="relative w-20 h-20 group">
                              <img src={photoSrc(url)} className="w-full h-full object-cover rounded-md border" alt="" />
                              <button onClick={() => removePhoto(ri, pi)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                            </div>
                          ))}
                          <label className="w-20 h-20 border-2 border-dashed border-slate-200 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors" data-testid={`upload-photo-${ri}`}>
                            <Camera className="w-5 h-5 text-slate-400" />
                            <span className="text-[10px] text-slate-400 mt-0.5">{tr("Add Photo")}</span>
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(ri, e.target.files[0])} />
                          </label>
                        </div>
                        {resp.allow_notes !== false && (
                          <div className="flex items-start gap-1.5 mt-2">
                            <Textarea value={resp.notes || ""} onChange={(e) => updateResp(ri, "notes", e.target.value)} placeholder={tr("Add notes...")} className="min-h-[44px] sm:min-h-[36px] text-sm sm:text-xs resize-y flex-1" rows={1} data-testid={`notes-${ri}`} />
                            <SpeechToTextButton
                              className="w-9 h-9 flex-shrink-0 mt-0.5"
                              disabled={isComplete}
                              onTranscript={(text) => updateResp(ri, "notes", ((resp.notes || "") + " " + text).trim())}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Text / Long Text field */}
                    {isTextField && (
                      <div className="mt-3 ml-6 sm:ml-8">
                        <div className="flex items-start gap-1.5">
                          <div className="flex-1">
                            {resp.rating_type === "long_text" ? (
                              <Textarea value={resp.value || ""} onChange={(e) => updateResp(ri, "value", e.target.value)} placeholder={tr("Enter notes...")} className="text-sm resize-y" rows={3} data-testid={`text-input-${ri}`} />
                            ) : (
                              <Input value={resp.value || ""} onChange={(e) => updateResp(ri, "value", e.target.value)} placeholder={tr("Enter value...")} className="text-sm" data-testid={`text-input-${ri}`} />
                            )}
                          </div>
                          <SpeechToTextButton
                            className="w-9 h-9 flex-shrink-0 mt-0.5"
                            disabled={isComplete}
                            onTranscript={(text) => updateResp(ri, "value", ((resp.value || "") + " " + text).trim())}
                          />
                        </div>
                      </div>
                    )}

                    {/* Photos & Notes row — for rating types only */}
                    {hasButtons && (
                      <div className="mt-3 ml-6 sm:ml-8 space-y-2">
                        {resp.allow_photos !== false && (
                          <div>
                            {needsPhoto && <span className="text-[10px] font-semibold text-red-500 uppercase tracking-wide">{tr("Photo required")}</span>}
                            <div className={`flex items-center gap-2 flex-wrap ${needsPhoto ? "p-1.5 border border-red-300 border-dashed rounded-md bg-red-50/50" : ""}`}>
                              {(resp.photos || []).map((url, pi) => (
                                // eslint-disable-next-line react/no-array-index-key -- photo URLs can repeat; removePhoto uses (ri, pi) tuple
                                <div key={`rp2-${pi}`} className="relative w-16 h-16 sm:w-14 sm:h-14 group">
                                  <img src={photoSrc(url)} className="w-full h-full object-cover rounded border" alt="" />
                                  <button onClick={() => removePhoto(ri, pi)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-[10px]"><X className="w-3 h-3" /></button>
                                </div>
                              ))}
                              <label className={`flex items-center gap-1.5 px-4 py-3 sm:px-3 sm:py-2 text-xs border border-dashed rounded-md cursor-pointer transition-colors ${needsPhoto ? "text-red-500 border-red-300 hover:border-red-400 hover:bg-red-50" : "text-slate-400 border-slate-200 hover:border-blue-400 hover:text-blue-500"}`} data-testid={`upload-photo-${ri}`}>
                                <Camera className="w-4 h-4 sm:w-3.5 sm:h-3.5" />{tr("Photo")}
                                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(ri, e.target.files[0])} />
                              </label>
                            </div>
                          </div>
                        )}
                        {resp.allow_notes !== false && (
                          <div>
                            {needsNote && <span className="text-[10px] font-semibold text-red-500 uppercase tracking-wide">{tr("Note required")}</span>}
                            <div className="flex items-start gap-1.5">
                              <Textarea
                                value={resp.notes || ""}
                                onChange={(e) => updateResp(ri, "notes", e.target.value)}
                                placeholder={tr("Add notes...")}
                                className={`min-h-[44px] sm:min-h-[36px] text-sm sm:text-xs resize-y flex-1 ${needsNote ? "border-red-300 bg-red-50/50 focus-visible:ring-red-400" : ""}`}
                                rows={1}
                                data-testid={`notes-${ri}`}
                              />
                              <SpeechToTextButton
                                className="w-9 h-9 flex-shrink-0 mt-0.5"
                                disabled={isComplete}
                                onTranscript={(text) => updateResp(ri, "notes", ((resp.notes || "") + " " + text).trim())}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-20 text-slate-400" data-testid="no-locations">
          <MapPin className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-lg font-medium">{tr("No locations added")}</p>
          <p className="text-sm mt-1 mb-4">{tr("Add your first inspection location to begin")}</p>
          <Button onClick={() => setShowAddLoc(true)}>
            <Plus className="w-4 h-4 mr-1" />{tr("Add Location")}
          </Button>
        </div>
      )}

      {/* Add Location Dialog */}
      <Dialog open={showAddLoc} onOpenChange={setShowAddLoc}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{tr("Add Location")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Location Name *</Label>
              <Input
                value={locForm.name}
                onChange={(e) => setLocForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Trash Chute 1"
                data-testid="input-loc-name"
              />
            </div>
            <div>
              <Label className="text-xs">{tr("Identifier (optional)")}</Label>
              <Input
                value={locForm.identifier}
                onChange={(e) => setLocForm((f) => ({ ...f, identifier: e.target.value }))}
                placeholder="e.g., C62"
                data-testid="input-loc-id"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLoc(false)}>{tr("Cancel")}</Button>
            <Button onClick={addLocation} data-testid="btn-confirm-add-loc">{tr("Add Location")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
